import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Wand2, GitBranch, History, Trash2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";
import { MermaidRenderer } from "@/components/roles/MermaidRenderer";

/** Diagram Studio: one-click SAP diagram templates built from this role's
 * real data (Three-Tier Role Model, SoD Conflict Matrix, Authorization
 * Concept, P2P/O2C process flows) plus a Reasoner-drafted custom mode for
 * free-form prompts, an iterative Refine mode, and per-role Diagram
 * History. Lives inside the Authorization Matrix Manager. */
export function DiagramsTab({ role }) {
    const [templates, setTemplates] = useState([]);
    const [generating, setGenerating] = useState("");
    const [customPrompt, setCustomPrompt] = useState("");
    const [refineInstruction, setRefineInstruction] = useState("");
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState([]);

    const loadHistory = useCallback(() => {
        if (!role?.id) return;
        api.get(`/roles/${role.id}/diagrams`).then(({ data }) => setHistory(data || [])).catch(() => setHistory([]));
    }, [role?.id]);

    useEffect(() => {
        api.get("/diagrams/templates").then(({ data }) => setTemplates(data)).catch(() => setTemplates([]));
        loadHistory();
    }, [loadHistory]);

    const runTemplate = async (templateId) => {
        setGenerating(templateId);
        try {
            const { data } = await api.post(`/diagrams/templates/${templateId}/generate`, { role_id: role?.id });
            setResult(data);
            loadHistory();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Diagram generation failed");
        } finally {
            setGenerating("");
        }
    };

    const runCustom = async () => {
        if (!customPrompt.trim()) { toast.error("Describe the diagram you want"); return; }
        setGenerating("custom");
        try {
            const { data } = await api.post("/diagrams/custom", { prompt: customPrompt, role_id: role?.id });
            setResult(data);
            loadHistory();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Diagram drafting failed");
        } finally {
            setGenerating("");
        }
    };

    const runRefine = async () => {
        if (!result) return;
        if (!refineInstruction.trim()) { toast.error("Describe the change you want, e.g. 'add a step'"); return; }
        setGenerating("refine");
        try {
            const { data } = await api.post("/diagrams/refine", {
                mermaid_code: result.mermaid_code, instruction: refineInstruction, title: result.title, role_id: role?.id,
            });
            setResult(data);
            loadHistory();
            if (data.refined === false) {
                toast.info("No changes suggested — try a more specific instruction");
            } else {
                setRefineInstruction("");
                toast.success("Diagram refined");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Refine failed");
        } finally {
            setGenerating("");
        }
    };

    const deleteHistoryEntry = async (id, e) => {
        e.stopPropagation();
        try {
            await api.delete(`/roles/${role.id}/diagrams/${id}`);
            setHistory((prev) => prev.filter((h) => h.id !== id));
        } catch (err) {
            toast.error("Failed to delete saved diagram");
        }
    };

    return (
        <div className="space-y-4">
            <p className="text-xs text-slate-500 font-mono">
                One-click SAP diagram templates, built directly from this role&rsquo;s real data — plus a
                Reasoner-drafted custom mode for anything else you need to visualize.
            </p>
            <div className="flex flex-wrap gap-2">
                {templates.map((t) => (
                    <button
                        key={t.id}
                        data-testid={TID.diagramTemplateBtn(t.id)}
                        onClick={() => runTemplate(t.id)}
                        disabled={!!generating}
                        title={t.description}
                        className="inline-flex items-center gap-1.5 px-3 py-2 panel text-xs font-mono text-slate-300 hover:text-cyan-300 hover:border-cyan-500/30 transition-colors disabled:opacity-50"
                    >
                        {generating === t.id ? <Loader2 size={13} className="animate-spin" /> : <GitBranch size={13} />}
                        {t.title}
                    </button>
                ))}
            </div>

            <div className="panel p-3 space-y-2">
                <div className="label-eyebrow">Custom Diagram (Reasoner-drafted)</div>
                <textarea
                    data-testid={TID.diagramCustomPromptInput}
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    rows={2}
                    placeholder="e.g. Show how this role's transactions map onto a swimlane approval flow"
                    className="w-full bg-[var(--bg-base)] border border-white/10 rounded-sm px-3 py-2 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none resize-y"
                />
                <div className="flex justify-end">
                    <button
                        data-testid={TID.diagramCustomGenerateBtn}
                        onClick={runCustom}
                        disabled={!!generating}
                        className="btn-primary sheen inline-flex items-center gap-2 text-xs disabled:opacity-60"
                    >
                        {generating === "custom" ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                        Draft with Reasoner
                    </button>
                </div>
            </div>

            {history.length > 0 && (
                <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 label-eyebrow"><History size={11} /> Diagram History</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {history.map((h) => (
                            <button
                                key={h.id}
                                data-testid={TID.diagramHistoryChip(h.id)}
                                onClick={() => setResult(h)}
                                title={h.title}
                                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-white/5 border border-white/10 text-[10px] font-mono text-slate-400 hover:text-cyan-300 hover:border-cyan-500/30 transition-colors w-full"
                            >
                                {/* Timestamp disambiguates the many same-template regenerations. */}
                                <span className="shrink-0 text-slate-400 tabular-nums">
                                    {h.created_at ? new Date(h.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                                </span>
                                <span className="truncate flex-1 text-left">{h.title}</span>
                                <Trash2
                                    size={10}
                                    data-testid={TID.diagramHistoryDeleteBtn(h.id)}
                                    onClick={(e) => deleteHistoryEntry(h.id, e)}
                                    className="text-slate-500 hover:text-rose-400 shrink-0"
                                />
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {result && (
                <>
                    <MermaidRenderer code={result.mermaid_code} title={result.title} />
                    <div className="panel p-3 space-y-2">
                        <div className="label-eyebrow flex items-center gap-1.5"><Sparkles size={11} /> Refine This Diagram</div>
                        <div className="flex gap-2">
                            <input
                                data-testid={TID.diagramRefineInput}
                                value={refineInstruction}
                                onChange={(e) => setRefineInstruction(e.target.value)}
                                placeholder="e.g. make this bigger, add a step between Create PO and Goods Receipt"
                                className="flex-1 bg-[var(--bg-base)] border border-white/10 rounded-sm px-3 py-2 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                            />
                            <button
                                data-testid={TID.diagramRefineBtn}
                                onClick={runRefine}
                                disabled={!!generating}
                                className="btn-primary sheen inline-flex items-center gap-1.5 text-xs px-3 disabled:opacity-60 shrink-0"
                            >
                                {generating === "refine" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                                Apply
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
